import React from 'react'
import { Button, Col, Container, Row } from 'react-bootstrap'
import { Search } from 'react-feather'
import { DateTime } from 'luxon'
import * as Request from '../../utilities/request'
import * as yup from 'yup'
import { AppContext } from '../../App'
import { Client } from '../../../../back-end/common/client'
import { Invoice, InvoiceLineAmountTypes } from '../../../../back-end/common/invoice'
import { Contact } from '../../../../back-end/common/contact'
import { FormText } from '../../components/ui/Form/Text'
import { FormSelect, SelectOption } from '../../components/ui/Form/Select'
import { FormDate } from '../../components/ui/Form/Date'
import { XeroOrg } from '../../../../back-end/common/xeroOrg'
import { AccountXeroOrg } from '../../../../back-end/common/accountXeroOrg'
import { useNavigate } from 'react-router-dom'
import { Loading } from '../../components/ui/Loading/Loading'
import { Formik } from 'formik'
import { Staff } from '../../../../back-end/common/staff'
import { Admin, Director, ExecAssistant } from '../../constants/permission'

type InvoiceContact = { firstName: string; lastName: string; email: string; id: string }

type NewInvoice = Omit<
	Invoice,
	| 'createTs'
	| 'createUser'
	| 'createUserID'
	| 'modifiedTs'
	| 'modifiedUserID'
	| 'number'
	| 'xeroID'
	| 'invoiceID'
	| 'client'
	| 'clientGroup'
	| 'xeroOrg'
	| 'contact'
	| 'accountID'
	| 'recordStatusID'
	| 'contacts'
	| 'fyiDocumentID'
	| 'isOriginalImport'
> & { contacts: InvoiceContact[] }

const newInvoice = (currentPeriodDate: string, defaultXeroOrg?: string | null): NewInvoice => ({
	contacts: [],
	clientID: '',
	clientGroupID: null,
	lineAmountTypes: 'Exclusive',
	date: DateTime.fromISO(currentPeriodDate).endOf('month').startOf('day').toISODate()!,
	dueDate: DateTime.fromISO(currentPeriodDate).endOf('month').startOf('day').plus({ days: 30 }).toISODate()!,
	xeroOrgID: defaultXeroOrg ?? '',
	status: 'DRAFT',
	invoiceApprovedByStaffID: null,
	sentToContact: false,
	minimumTotal: 0,
	amountDue: 0,
	amountPaid: 0,
	subTotal: 0,
	totalTax: 0,
	total: 0,
	writtenOff: 0,
	writtenUp: 0,
	carryForward: 0,
	serviceFee: 0,
})

const schema = yup.object().shape({
	contacts: yup
		.array(
			yup.object().shape({
				firstName: yup.string().required('Must have first and last names'),
				lastName: yup.string().required('Must have first and last name'),
				email: yup.string().required('Must have an email'),
			})
		)
		.min(0)
		.max(5)
		.required(),
	clientID: yup.string().required('Client is required').uuid(),
	clientGroupID: yup.string().nullable().uuid(),
	lineAmountTypes: yup.string().oneOf(['Inclusive', 'Exclusive', 'NoTax']).required(),
	date: yup.date().required('Date is required').min(new Date('2001-01-01T00:00:00.000Z'), 'Date is required'),
	dueDate: yup.date().required('Due Date is required').min(new Date('2001-01-01T00:00:00.000Z'), 'Due Date is required'),
	xeroOrgID: yup.string().required('Invoice Company is required').uuid(),
})

interface NewInvoiceScreenProps {
	editInvoice?: Invoice
	setEditInvoice?: (value: Invoice | null) => void
	closeModal?: () => void
}

const NewInvoiceScreen = (props?: NewInvoiceScreenProps) => {
	const navigate = useNavigate()
	const { appState } = React.useContext(AppContext)

	const [clients, setClients] = React.useState<Client[] | null>(null)
	const [staff, setStaff] = React.useState<Staff[] | null>(null)
	const [contacts, setContacts] = React.useState<Contact[] | null>(null)
	const [xeroOrgs, setXeroOrgs] = React.useState<XeroOrg[] | null>(null)

	const isEditing =
		props && !(props.editInvoice === undefined || props.editInvoice === null) && !(props.setEditInvoice === undefined || props.setEditInvoice === null)
	const editableProperties = ['date', 'dueDate', 'contacts']
	const defaultXeroOrg = appState.accounts.find((acc) => acc.accountID === appState.authState.attributes?.['custom:currentAccountId'])?.billySettings
		?.defaultInvoiceXeroOrgID

	React.useEffect(() => {
		const getData = async () => {
			const [xeroOrgsRes, staffRes] = await Promise.all([
				Request.get(`organisation?where=AccountXeroOrgIsBilly==1&where=XeroOrgTenantType==ORGANISATION`),
				Request.get(`staff?where=BillyPermissionID=in(${Admin},${Director},${ExecAssistant})`),
				Request.getPagedData(`client`, 4000, (data) => setClients((clients) => [...(clients || []), ...(data as { clients: Client[] }).clients])),
			])

			setXeroOrgs(xeroOrgsRes.data.accountXeroOrgs.map((axo: AccountXeroOrg) => axo.xeroOrg))
			setStaff(staffRes.data.staff)
		}

		if (appState.authState.isLoggedIn) {
			getData()
		}
	}, [appState.authState.isLoggedIn, appState.authState.apiToken])

	const currentPeriod = appState.accounts.find((acc) => acc.accountID === appState.authState.attributes?.['custom:currentAccountId'])?.billySettings
		?.agedWipDate

	if (clients === null || xeroOrgs === null || !currentPeriod) {
		return (
			<div className="center-flex p-5">
				<Loading show />
			</div>
		)
	}

	const handleSubmit = async (values: NewInvoice) => {
		if (isEditing) {
			try {
				const originalValues = props.editInvoice!
				const difference = Object.entries(values).reduce((a: { [key: string]: unknown }, [key, val]) => {
					if (key in originalValues && originalValues[key as keyof typeof originalValues] !== val && editableProperties.includes(key)) {
						a[key] = val
					}
					return a
				}, {})
				const res = await Request.put(`invoice/${props.editInvoice!.invoiceID}`, difference)
				props.setEditInvoice?.(res.data.invoices[0])
				props.closeModal?.()
			} catch (err) {
				console.log(err)
			}
		} else {
			try {
				const invoiceRes = await Request.post(`invoice`, values)
				const invoiceID = invoiceRes.data.invoices[0].invoiceID

				if (!invoiceID) {
					// message alert
				} else {
					navigate(`/invoice/${invoiceID}/allocation`)
				}
			} catch (err) {
				console.log(err)
			}
		}
	}

	return (
		<Container>
			<Formik
				initialValues={isEditing ? (props.editInvoice! as unknown as NewInvoice) : newInvoice(currentPeriod, defaultXeroOrg)}
				onSubmit={handleSubmit}
				validationSchema={schema}
				validateOnMount={false}
				validateOnChange={false}
			>
				{({ values, errors, handleSubmit, isSubmitting, setFieldValue }) => {
					const client = clients.find((client) => client.clientID === values.clientID)

					const getClientContacts = React.useCallback(
						async (clientID: string) => {
							const contactsRes = await Request.get(`contact?where=ClientID==${clientID}`)
							setContacts(contactsRes.data.contacts)
							if (contactsRes.data.contacts.length > 0) {
								setFieldValue(
									'contacts',
									contactsRes.data.contacts
										.slice(0, 5)
										.map((c: Contact) => ({
											firstName: c.name.split(' ').slice(0, -1).join(' '),
											lastName: c.name.split(' ').at(-1),
											email: c.email || client?.email,
											id: c.contactID,
										}))
										.filter((c: InvoiceContact) => c.email)
								)
							}
						},
						[setContacts, appState.authState]
					)

					return (
						<Row>
							<Col className={props?.closeModal ? '' : 'pt-5'}>
								<Row>
									<Col>
										<h2>{isEditing ? 'Edit' : 'Create'} Invoice</h2>
									</Col>
								</Row>
								<Row className="pt-2">
									<Col sm={6}>
										<FormSelect
											name="client"
											label="Invoiced To"
											isDisabled={isEditing}
											placeholder={
												<>
													<Search size={14} />
													<span className="pl-2">Search or select a client</span>
												</>
											}
											onChange={(value) => {
												if (value) {
													const clientID = (value as SelectOption<string>).value
													setFieldValue('clientID', clientID)
													setFieldValue('contacts', [])
													getClientContacts(clientID)
												}
											}}
											options={clients.map((client) => ({ ...client, label: client.name, value: client.clientID })) || []}
											value={values.clientID}
											feedback={errors.clientID}
										/>
									</Col>
									{/* <Col sm={3}>
										<FormLabel>&nbsp;</FormLabel>
										<FormCheck
											checked={!values.clientGroupID}
											className="pt-2"
											name="invoiceWho"
											label="Invoice the selected client"
											radioGroup="invoiceWho"
											type="radio"
											disabled={!values.clientID}
											onChange={() => setFieldValue('clientGroupID', null)}
										/>
									</Col>
									<Col sm={3}>
										<FormLabel>&nbsp;</FormLabel>
										<FormCheck
											checked={!!values.clientGroupID}
											className="pt-2"
											name="invoiceWho"
											label="Invoice the parent client"
											radioGroup="invoiceWho"
											type="radio"
											disabled={!values.clientID}
											onChange={() => setFieldValue('clientGroupID', client!.clientGroup!.clientGroupID)}
										/>
									</Col> */}
								</Row>
								<Row className="pt-1">
									<Col>
										{client && (
											<span>
												Note: Client <strong>{client.name}</strong> is part of <strong>{client.clientGroup?.name}</strong> group
											</span>
										)}
									</Col>
								</Row>
								<Row className="pt-3">
									<Col sm={12}>
										<FormSelect
											name="contacts"
											label="Contacts (to receive invoice when emailed)"
											placeholder={
												<>
													<Search size={14} />
													<span className="pl-2">Search or select people</span>
												</>
											}
											onChange={(value) => {
												console.log('value', value)
												if (value && Array.isArray(value)) {
													setFieldValue(
														'contacts',
														value.map((v) => ({ ...v, id: v.value }))
													)
												}
											}}
											options={(
												contacts?.map((contact) => ({
													firstName: contact.name.split(' ').slice(0, -1).join(' '),
													lastName: contact.name.split(' ').at(-1),
													email: contact.email,
													label: `${contact.name} (${contact.email || 'No Email'})`,
													value: contact.contactID,
												})) || []
											).concat(
												staff?.map((s) => ({
													firstName: s.name.split(' ').slice(0, -1).join(' '),
													lastName: s.name.split(' ').at(-1),
													email: s.email,
													label: `${s.name} (${s.email || 'No Email'})`,
													value: s.staffID,
												})) || []
											)}
											value={values.contacts.map((c) => c.id)}
											feedback={
												Array.isArray(errors.contacts)
													? errors.contacts
															.map((c) =>
																typeof c === 'string'
																	? c
																	: `${c.firstName || ''}${c.firstName && (c.lastName || c.email) ? ', ' : ''}${
																			c.lastName || ''
																	  }${c.lastName && c.email ? ', ' : ''}${c.email || ''}`
															)
															.join(', ')
													: errors.contacts
											}
											isClearable
											isSearchable
											isMulti
										/>
									</Col>
								</Row>
								<Row className="pt-3">
									<Col sm={6}>
										<FormText
											placeholder="Address line 1"
											name="address1"
											label="Client Invoicing Address"
											disabled={true}
											value={client?.postalAddress || ''}
										/>
									</Col>
									<Col sm={6}>
										<FormText placeholder="Address line 2" name="address2" label="&nbsp;" disabled={true} value={''} />
									</Col>
								</Row>
								<Row className="pt-3">
									<Col sm={6}>
										<FormText placeholder="City" name="city" disabled={true} value={client?.postalCity || ''} />
									</Col>
									<Col sm={3}>
										<FormText placeholder="State" name="region" disabled={true} value={client?.postalRegion || ''} />
									</Col>
									<Col sm={3}>
										<FormText placeholder="Postcode" name="postcode" disabled={true} value={client?.postalPostCode || ''} />
									</Col>
								</Row>
								<Row className="pt-3">
									<Col sm={6}>
										<FormText placeholder="Country" name="country" disabled={true} value={client?.postalCountry || ''} />
									</Col>
								</Row>
								<Row className="pt-3">
									<Col sm={6}>
										<FormSelect
											name="xeroOrg"
											label="Invoiced by Company"
											isDisabled={isEditing}
											placeholder={
												<>
													<Search size={14} />
													<span className="pl-2">Search or select a company</span>
												</>
											}
											onChange={(value) => setFieldValue('xeroOrgID', value ? (value as SelectOption<string>).value : '')}
											options={xeroOrgs.map((xo) => ({ ...xo, label: xo.name!, value: xo.xeroOrgID! })) || []}
											value={values.xeroOrgID}
											feedback={errors?.xeroOrgID}
											isClearable
										/>
									</Col>
									<Col sm={6}>
										<FormSelect
											name="taxtype"
											label="Tax Type"
											isDisabled={isEditing}
											placeholder={
												<>
													<Search size={14} />
													<span className="pl-2">Search or select a tax type</span>
												</>
											}
											onChange={(value) =>
												setFieldValue(
													'lineAmountTypes',
													value
														? ((value as SelectOption<unknown>).value as unknown as InvoiceLineAmountTypes)
														: ('Exclusive' as const)
												)
											}
											options={[
												{
													label: 'Exclusive',
													value: 'Exclusive',
												},
												{
													label: 'No Tax',
													value: 'NoTax',
												},
											]}
											value={String(values.lineAmountTypes)}
											feedback={errors?.lineAmountTypes}
											isClearable
										/>
									</Col>
								</Row>
								<Row className="pt-3">
									<Col sm={6}>
										<FormDate
											name="postcode"
											label="Invoice Date"
											onChange={(e) => {
												const [year, month, date] = e.target.value.split('-').map((str) => Number(str))
												const selectedDate = DateTime.utc(year, month, date)
												if (selectedDate.isValid) {
													setFieldValue('date', selectedDate.toISODate())
													setFieldValue('dueDate', selectedDate.plus({ days: 30 }).toISODate())
												}
											}}
											feedback={errors?.date}
											min={DateTime.fromISO(currentPeriod).toFormat('yyyy-MM-dd')}
											value={DateTime.fromISO(values.date).toFormat('yyyy-MM-dd')}
										/>
									</Col>
									<Col sm={6}>
										<FormDate
											name="postcode"
											label="Due Date"
											onChange={(e) => {
												const [year, month, date] = e.target.value.split('-').map((str) => Number(str))
												const selectedDate = DateTime.utc(year, month, date)
												if (selectedDate.isValid) {
													setFieldValue('dueDate', selectedDate.toISODate())
												}
											}}
											feedback={errors?.dueDate}
											value={DateTime.fromISO(values.dueDate).toFormat('yyyy-MM-dd')}
										/>
									</Col>
								</Row>
								<Row className="pt-3">
									<Col />
									<Col style={{ width: '100%', display: 'flex' }}>
										<Button
											variant="secondary"
											onClick={() => (props && props.closeModal ? props.closeModal() : navigate('/invoices'))}
											style={{ marginLeft: 'auto', marginRight: '20px' }}
										>
											Cancel
										</Button>
										<Button variant="primary" disabled={isSubmitting} onClick={() => handleSubmit()}>
											Save {isEditing ? '' : 'Draft & Continue'}
										</Button>
									</Col>
								</Row>
							</Col>
						</Row>
					)
				}}
			</Formik>
		</Container>
	)
}

export default NewInvoiceScreen
